import type { CleaningCompletionReportApartmentDetails } from '@/shared/api';
import { formatDate, formatDateTime, formatNumber } from '@/shared/lib/format';
import { describeQuality, evidenceLabel, guestsLabel } from './completionReportModel';

const CARD = 'border-hairline rounded-tile flex flex-col gap-3 border bg-white p-4';

/** Detail eines Apartments: Summen und jede abgeschlossene Reinigung mit Person, Gästen, Fotos. */
export function CompletionReportDetailPanel({
  apartment,
}: {
  apartment: CleaningCompletionReportApartmentDetails | null;
}) {
  if (!apartment)
    return (
      <aside className={CARD}>
        <b className="font-serif text-lg">Apartment wählen</b>
        <p className="text-ink-soft text-sm">
          Wähl eine Zeile oder einen Balken, um die einzelnen Reinigungen nachzuvollziehen.
        </p>
      </aside>
    );
  const name = apartment.operationalName ?? apartment.name;
  const quality = describeQuality(apartment.totals);
  const facts = [
    ['Abgeschlossene Reinigungen', formatNumber(apartment.totals.cleaningCount)],
    ['Erfasste Gäste', guestsLabel(apartment.totals)],
    ['Fotonachweise', evidenceLabel(apartment.totals)],
    [
      'Offene Meldungen',
      apartment.totals.openIssueCount === 0
        ? 'keine'
        : formatNumber(apartment.totals.openIssueCount),
    ],
  ] as const;
  return (
    <aside aria-label={`Detail ${name}`} className={CARD}>
      <header>
        <b className="font-serif text-xl">{name}</b>
        {apartment.operationalName && <p className="text-ink-soft text-xs">{apartment.name}</p>}
      </header>
      <dl className="border-hairline divide-hairline rounded-control divide-y border">
        {facts.map(([label, value]) => (
          <div key={label} className="flex items-center justify-between gap-3 px-3 py-2.5 text-sm">
            <dt className="text-ink-soft">{label}</dt>
            <dd className="font-semibold tabular-nums">{value}</dd>
          </div>
        ))}
      </dl>
      {quality.length > 0 && (
        <p className="text-ink-soft text-xs">Datenlage der Gäste: {quality.join(' · ')}</p>
      )}
      <h3 className="font-serif text-base font-bold">Abgeschlossene Reinigungen</h3>
      <ol className="border-hairline divide-hairline rounded-control divide-y border">
        {apartment.cleanings.map((cleaning) => (
          <li key={cleaning.cleaningTaskId} className="flex flex-col gap-1 px-3 py-2.5 text-xs">
            <span className="flex items-start justify-between gap-3">
              <b className="text-sm">{formatDate(cleaning.scheduledFor)}</b>
              <span className="text-ink-soft text-right">
                erfasst {formatDateTime(cleaning.capturedAt)}
              </span>
            </span>
            <span className="text-ink-soft">{cleaning.assignedToUserName}</span>
            <span className="text-ink-soft flex flex-wrap gap-x-3">
              <span>
                Gäste:{' '}
                {cleaning.guestCount === null
                  ? 'nicht vollständig erfasst'
                  : formatNumber(cleaning.guestCount)}
              </span>
              <span>
                Fotos:{' '}
                {cleaning.photoSlotCount === 0
                  ? 'nicht verlangt'
                  : `${cleaning.filledPhotoSlotCount} von ${cleaning.photoSlotCount}`}
              </span>
              {cleaning.hasOpenIssue && (
                <span className="text-sealwax font-semibold">Meldung offen</span>
              )}
            </span>
          </li>
        ))}
      </ol>
    </aside>
  );
}
